/**
 * Tracks how far into the app's own history the WebView is, so back
 * controls know whether history.back() stays in the app or whether to fall
 * back to the tab root (e.g. a screen opened from a push or a cold start).
 *
 * Each history entry is stamped with its depth in history.state, so it
 * survives back/forward and full page loads (which some flows still do).
 */

import { getOwningTab } from 'helpers/appNavigation';

const DEPTH_KEY = 'twineAppDepth';
const LAST_DEPTH_KEY = 'twine-app-history-depth';

let depth = 0;

function readEntryDepth() {
  const state = window.history.state;
  return state && typeof state[DEPTH_KEY] === 'number'
    ? state[DEPTH_KEY]
    : null;
}

function stampEntry() {
  try {
    window.history.replaceState(
      { ...(window.history.state || {}), [DEPTH_KEY]: depth },
      ''
    );
  } catch (e) {
    // Some WebViews throttle replaceState; depth just stays in memory.
  }
  try {
    window.sessionStorage.setItem(LAST_DEPTH_KEY, String(depth));
  } catch (e) {
    // Storage unavailable
  }
}

function cameFromInApp() {
  try {
    const [entry] = window.performance.getEntriesByType('navigation');
    if (entry && entry.type !== 'navigate') return false;
    return (
      !!document.referrer &&
      new URL(document.referrer).origin === window.location.origin
    );
  } catch (e) {
    return false;
  }
}

/** Call once with the router's history. Returns an unlisten function. */
export function trackAppHistory(history) {
  const entryDepth = readEntryDepth();
  if (entryDepth !== null) {
    depth = entryDepth;
  } else {
    let lastDepth = 0;
    try {
      lastDepth = Number(window.sessionStorage.getItem(LAST_DEPTH_KEY)) || 0;
    } catch (e) {
      // Storage unavailable
    }
    depth = cameFromInApp() ? lastDepth + 1 : 0;
    stampEntry();
  }

  return history.listen((location, action) => {
    if (action === 'PUSH') {
      depth += 1;
      stampEntry();
    } else if (action === 'REPLACE') {
      stampEntry();
    } else {
      const popped = readEntryDepth();
      depth = popped !== null ? popped : Math.max(depth - 1, 0);
    }
  });
}

export function canGoBackInApp() {
  return depth > 0;
}

/** Back from a pushed screen: in-app history if there is any, else its tab root. */
export function goBackInApp(history, pathname, user) {
  if (canGoBackInApp()) {
    history.goBack();
    return;
  }
  const tab = getOwningTab(pathname, user);
  // Signed out there are no tabs: the start screen is home.
  history.replace(tab ? tab.path : '/app');
}
